import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, BadgeCheck } from "lucide-react";
import { TESTIMONIALS } from "@/lib/data";

// "In their words" → repurposed as a client showcase carousel. Each slide
// features a single client name, large and centred, with a subtle "Trusted
// partner" eyebrow. Auto-advances every 4 seconds and is navigable manually.
export default function TestimonialCarousel() {
    const [idx, setIdx] = useState(0);
    const total = TESTIMONIALS.length;

    useEffect(() => {
        const t = setInterval(() => setIdx((i) => (i + 1) % total), 4000);
        return () => clearInterval(t);
    }, [total]);

    const go = (delta) => setIdx((i) => (i + delta + total) % total);
    const current = TESTIMONIALS[idx];

    return (
        <div data-testid="testimonial-carousel" className="relative max-w-4xl mx-auto">
            <div className="card-3d p-10 md:p-16 text-center relative overflow-hidden">
                <span className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] bg-brand-creamLight text-brand-amber border border-brand-border">
                    <BadgeCheck size={14} strokeWidth={2} />
                    Trusted partner
                </span>
                <p
                    key={current.name}
                    data-testid="testimonial-client-name"
                    className="font-serif text-3xl md:text-5xl leading-tight text-brand-ink min-h-[120px] md:min-h-[140px] mt-10 flex items-center justify-center animate-fade-in"
                >
                    {current.name}
                </p>

                <div className="flex items-center justify-center gap-2 mt-8" role="tablist">
                    {TESTIMONIALS.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setIdx(i)}
                            aria-label={`Show client ${i + 1}`}
                            aria-selected={i === idx}
                            role="tab"
                            data-testid={`testimonial-dot-${i}`}
                            className={`h-1.5 rounded-full transition-all ${
                                i === idx ? "w-8 bg-brand-ink" : "w-2 bg-brand-ink/25"
                            }`}
                        />
                    ))}
                </div>

                <p className="mt-6 text-xs text-brand-muted">
                    {idx + 1} of {total} active client partnerships
                </p>
            </div>

            <button
                aria-label="Previous client"
                onClick={() => go(-1)}
                data-testid="testimonial-prev"
                className="hidden md:grid absolute -left-6 top-1/2 -translate-y-1/2 w-12 h-12 place-items-center rounded-full bg-white border border-brand-border text-brand-ink hover:bg-brand-creamLight transition-colors"
            >
                <ChevronLeft size={20} />
            </button>
            <button
                aria-label="Next client"
                onClick={() => go(1)}
                data-testid="testimonial-next"
                className="hidden md:grid absolute -right-6 top-1/2 -translate-y-1/2 w-12 h-12 place-items-center rounded-full bg-white border border-brand-border text-brand-ink hover:bg-brand-creamLight transition-colors"
            >
                <ChevronRight size={20} />
            </button>
        </div>
    );
}
